import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getNotifications, markRead } from '../api/notifications';
import type { NotificationDto } from '../api/notifications';
import { markUnreadCountRead } from '../hooks/useUnreadCount';
import { Spinner } from '../components/Spinner';
import { formatEnumLabel } from '../utils/format';

const PAGE_SIZE = 50;

function formatCategory(category: string): string {
  return formatEnumLabel(category);
}

function formatDay(tick: number): string {
  return `Day ${tick}`;
}

export function RecentNews() {
  const { worldId, provinceId } = useAuth();
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [oldestTick, setOldestTick] = useState<number | undefined>(undefined);

  const loadPage = useCallback(async (before?: number) => {
    if (!worldId || !provinceId) return;
    setIsLoading(true);
    setError(null);
    const result = await getNotifications(worldId, provinceId, {
      before,
      limit: PAGE_SIZE,
    });
    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      const items = result.data;
      setNotifications((prev) => before != null ? [...prev, ...items] : items);
      setHasMore(items.length === PAGE_SIZE);
      if (items.length > 0) {
        setOldestTick(items[items.length - 1].tick);
      }
    }
    setIsLoading(false);
  }, [worldId, provinceId]);

  // Mark all visible notifications read on mount
  useEffect(() => {
    if (!worldId || !provinceId) return;
    void (async () => {
      await loadPage(undefined);
    })();
  }, [loadPage]);

  useEffect(() => {
    if (!worldId || !provinceId || notifications.length === 0) return;
    const latestTick = notifications[0].tick;
    void markRead(worldId, provinceId, { beforeTick: latestTick }).then((result) => {
      // SF-155 Component 7: push the shared unread-count cache to 0 immediately once mark-read
      // resolves successfully, instead of leaving Sidebar's badge and World Hub's Recent News card
      // stale for up to ~30s+jitter until the next poll tick. `markRead` never rejects (errors
      // surface via `result.error`, per this API module's convention), so a failed call correctly
      // leaves the shared cache untouched rather than lying about what got acknowledged.
      if (!result.error) {
        markUnreadCountRead();
      }
    });
  }, [worldId, provinceId, notifications]);

  const handleLoadMore = () => {
    if (oldestTick != null) {
      void loadPage(oldestTick);
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 760 }}>
      <h1 style={{ color: '#f0e8dc', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
        Recent News
      </h1>
      <p style={{ color: '#b5a99c', fontSize: 14, marginBottom: 24 }}>
        Events affecting your province
      </p>

      {isLoading && notifications.length === 0 && <Spinner />}

      {error && (
        <div style={{ color: '#c45a4a', fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!isLoading && !error && notifications.length === 0 && (
        <div style={{ color: '#b5a99c', fontSize: 14 }}>No notifications yet.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {notifications.map((n) => (
          <div
            key={n.id}
            style={{
              padding: '10px 16px',
              backgroundColor: n.readAt == null ? '#1e1c14' : '#171412',
              borderLeft: n.readAt == null ? '2px solid #d4b85f' : '2px solid transparent',
              borderBottom: '1px solid #2e2924',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 2 }}>
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#6a9e8e',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
                {formatCategory(n.category)}
              </span>
              <span style={{ fontSize: 11, color: '#b5a99c' }}>
                {formatDay(n.tick)}
              </span>
              {n.readAt == null && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor: '#d4b85f',
                  color: '#0e0c0a',
                  borderRadius: 4,
                  padding: '1px 5px',
                }}>
                  NEW
                </span>
              )}
            </div>
            <div style={{
              fontSize: 13,
              color: n.readAt == null ? '#f0e8dc' : '#b5a99c',
              fontWeight: n.readAt == null ? 500 : 400,
            }}>
              {n.summary}
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={handleLoadMore}
          disabled={isLoading}
          style={{
            marginTop: 16,
            padding: '8px 20px',
            backgroundColor: '#24201b',
            color: '#b5a99c',
            border: '1px solid #2e2924',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          {isLoading ? 'Loading...' : 'Load older'}
        </button>
      )}
    </div>
  );
}
